import {useRef} from 'react';
import * as React from 'react';

import {
    getCurrentLocale,
    getLocalizedLandOwnership,
    getLocalizedPrice
} from 'features/services/utils/env-helper';
import {
    getConstructionFormName,
    getDisposalFormName
} from 'features/services/utils/name-helpers';
import {LOCALE} from 'features/ui/enums';
import {renderInhabitDate} from 'features/services/utils/date-helpers';
import {DisposalForm, ResidenceType} from 'features/search/filters/enums';
import {getFormattedMessage} from 'features/services/utils/language-helper';

import './style.scss';

import { Facts } from '.';
import {QuickFactsItem} from './item';

interface Props {
    showMore?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- viewModel varies by page type
    viewModel: any;

    addressString: string;
    areaString: string;
    energyMarking: string;
    roomString: string;

    onFeeClick?: (feeString: string, index: number | string) => ()  => void;
    onCoOwningClick?: (index: number | string) => () => void;
    renderAreaInfoNorway: (areaType: string) => () => void;
}

export const StageQuickFacts: React.FunctionComponent<Props> = ({ 
    showMore, viewModel, 
    addressString, areaString, energyMarking, roomString, 
    onFeeClick, onCoOwningClick, renderAreaInfoNorway 
}) => {
    const priceButtonRef = useRef<HTMLButtonElement>(null);
    const feeButtonRef = useRef<HTMLButtonElement>(null);
    const areaButtonRef = useRef<HTMLButtonElement>(null);
    const numberOfVisibleItems = 5;

    const totalAmountString = (typeof viewModel.jmoyTotalAmountMin === 'undefined' || viewModel.jmoyTotalAmountMin === null)
        ? `${getLocalizedPrice(viewModel.jmoyTotalAmountMax)}`
        : (typeof viewModel.jmoyTotalAmountMax === 'undefined' || viewModel.jmoyTotalAmountMax === null)
            ? `${getLocalizedPrice(viewModel.jmoyTotalAmountMin)}`
            : (viewModel.jmoyTotalAmountMin === viewModel.jmoyTotalAmountMax)
                ? `${getLocalizedPrice(viewModel.jmoyTotalAmountMin)}`
                : `${getLocalizedPrice(viewModel.jmoyTotalAmountMin)} - ${getLocalizedPrice(viewModel.jmoyTotalAmountMax)}`;

    const feeString = (viewModel.minFee === viewModel.maxFee)
        ? `${getLocalizedPrice(viewModel.minFee)}`
        : `${getLocalizedPrice(viewModel.minFee)} - ${getLocalizedPrice(viewModel.maxFee)}`
    ;

    const showMinMaxFee = () => {
        if((viewModel.constructionForm === ResidenceType.DETACHED_HOUSE ||
            viewModel.constructionForm === ResidenceType.HOUSE ||
            viewModel.constructionForm === ResidenceType.TOWN_HOUSE ||
            viewModel.constructionForm === ResidenceType.SEMI_DETACHED_HOUSE) &&
            (viewModel.disposalForm === DisposalForm.OWNERSHIP || viewModel.disposalForm === DisposalForm.OWNERSHIP_APARTMENT)) return false;
        if(viewModel.isSoldOut) return false;
        if(typeof viewModel.minFee === 'undefined' || viewModel.minFee === null) return false;
        if(typeof viewModel.maxFee === 'undefined' || viewModel.maxFee === null) return false;
        if(viewModel.minFee === 0) return false;

        return true;
    };

    const facts : Facts[] = [
        {key: 'address', devCounter: '01',//(26)  
            label: getFormattedMessage('jm.components.QuickFacts.address'), 
            fact: addressString, 
            condition: addressString 
        },
        {key: 'constructionForm', devCounter: '02',//(21)  
            label: getFormattedMessage('jm.components.QuickFacts.constructionForm'), 
            fact: getConstructionFormName(viewModel.constructionForm as ResidenceType), 
            condition: viewModel.constructionForm 
        },
        {key: 'coOwning', devCounter: '03',//(49)  
            label: getFormattedMessage('jm.components.QuickFacts.coOwningPossibleLabel'), 
            fact: getFormattedMessage('jm.components.QuickFacts.coOwningPossibleFact'), 
            condition: viewModel.coOwningPossible 
        },
        {key: 'disposalForm', devCounter: '04',//(22)  
            label: getFormattedMessage('jm.components.QuickFacts.disposalForm'), 
            fact: getDisposalFormName(viewModel.disposalForm as DisposalForm), 
            condition: viewModel.disposalForm 
        },
        {key: 'elevator', devCounter: '05',//(10)  
            label: getFormattedMessage('jm.components.QuickFacts.elevator'), 
            fact: getFormattedMessage('jm.components.QuickFacts.yes'), 
            condition: viewModel.constructionForm === ResidenceType.APARTMENT
        },
        {key: 'energyMarking', devCounter: '06',//(19)  
            label: getFormattedMessage('jm.components.QuickFacts.energyMarking'), 
            fact: energyMarking, 
            condition: energyMarking && (!viewModel.swanLabel || getCurrentLocale() === LOCALE.NO)
        },

        {key: 'fiEnergyClass', devCounter: '07',//(47)  
            label: 'Energialuokka', 
            fact: viewModel.energyClass, 
            condition: viewModel.energyClass
        },
        {key: 'fiLandOwnerShip', devCounter: '08',//(42)  
            label: getFormattedMessage('jm.components.QuickFacts.landOwnership'), 
            fact: getLocalizedLandOwnership(viewModel.jmoyLandOwning), 
            condition: viewModel.jmoyLandOwning
        },
        {key: 'fiMinMaxTotalAmount', devCounter: '09',//(35)  
            label: 'Kokonaishinta', 
            fact: totalAmountString, 
            tooltip:'Velaton hinta + vuokratontin lunastus',
            condition: viewModel.jmoyTotalAmountMin && viewModel.jmoyTotalAmountMax
        },
        {key: 'fiMinMaxTotalPrice', devCounter: '10',//(33)  
            label: 'Velaton hinta', 
            fact: getLocalizedPrice(viewModel.jmoyTotalPriceMin) + ' - ' + getLocalizedPrice(viewModel.jmoyTotalPriceMax),
            tooltip:'Myyntihinta + lainaosuus', 
            condition: !viewModel.isSoldOut && viewModel.jmoyTotalPriceMin && viewModel.jmoyTotalPriceMax && viewModel.jmoyTotalPriceMin !== 0
        },

        {key: 'inhabitDate', devCounter: '11',//(23)  
            label: getFormattedMessage('jm.components.QuickFacts.inhabitDate'), 
            fact: renderInhabitDate(
                viewModel.inhabitDate, 
                viewModel.inhabitDateEnd, 
                viewModel.inhabitDateDetermined, 
                false, 
                false, 
                viewModel.inhabitDateRenderFull
            ), 
            condition: viewModel.inhabitDate 
        },
        {key: 'minMaxArea', devCounter: '12',//(11)  
            label: getCurrentLocale() === LOCALE.NO
                ? getFormattedMessage('jm.components.QuickFacts.noBRAi')
                : getFormattedMessage('jm.components.QuickFacts.area'), 
            fact: areaString, 
            onClick: getCurrentLocale() === LOCALE.NO
                ? renderAreaInfoNorway('bra-i')
                : undefined,
            ref: areaButtonRef,
            condition: Boolean(areaString) 
        },
        {key: 'minMaxFee', devCounter: '13',//(04)  
            label:  viewModel.disposalForm === DisposalForm.OWNERSHIP ||
                    viewModel.disposalForm === DisposalForm.OWNERSHIP_APARTMENT
                        ? getFormattedMessage('jm.components.QuickFacts.communityFee')
                        : getFormattedMessage('jm.components.QuickFacts.fee'), 
            fact: feeString, 
            onClick: onFeeClick 
                ? onFeeClick(feeString, 'minMaxFee') 
                : undefined,
            ref: feeButtonRef,
            condition: showMinMaxFee()
        },
        {key: 'minMaxPrice', devCounter: '14',//(01)  
            label: getFormattedMessage('jm.components.QuickFacts.price'), 
            fact: (viewModel.minPrice === viewModel.maxPrice) 
                ? `${getLocalizedPrice(viewModel.minPrice)}` 
                : `${getLocalizedPrice(viewModel.minPrice)} - ${getLocalizedPrice(viewModel.maxPrice)}`, 
            tooltip: getCurrentLocale() === LOCALE.FI
                ? 'Ennen muuttoa maksettava kauppahinta'
                : undefined,
            onClick: onCoOwningClick 
                ? onCoOwningClick('coOwning') 
                : undefined,
            ref: priceButtonRef,
            condition: !viewModel.isSoldOut && viewModel.minPrice && viewModel.minPrice !== 0 && viewModel.maxPrice 
        },
        {key: 'minMaxRooms', devCounter: '15',//(06)  
            label: getFormattedMessage('jm.components.QuickFacts.rooms'), 
            fact: roomString, 
            condition: roomString
        },
        {key: 'numberOfObjects', devCounter: '16',//(18)  
            label: getFormattedMessage('jm.components.QuickFacts.numberOfObjects'), 
            fact: viewModel.nrOfObjects, 
            condition: (viewModel.nrOfObjects !== 0 && viewModel.nrOfObjects !== null && typeof viewModel.nrOfObjects !== 'undefined') 
        },
        {key: 'nrOfParkingSpots', devCounter: '17',  
            label: getFormattedMessage('jm.components.QuickFacts.parkingSpots'), 
            fact: viewModel.nrOfParkingSpots, 
            condition: (viewModel.nrOfParkingSpots !== 0 && viewModel.nrOfParkingSpots !== null && typeof viewModel.nrOfParkingSpots !== 'undefined') 
        },
        {key: 'plotArea', devCounter: '18',//(15)  
            label: getFormattedMessage('jm.components.QuickFacts.plot'), 
            fact: viewModel.plotArea, 
            condition: viewModel.plotArea 
        },
        {key: 'swanLabel', devCounter: '19',//(20)  
            label: getFormattedMessage('jm.components.QuickFacts.swanLabel'), 
            fact: getFormattedMessage('jm.components.QuickFacts.yes'), 
            condition: viewModel.swanLabel 
        }
    ];

    const renderFactsByOrder = (factOrder: string[]) => {
        let factIndex = 0;
        return factOrder.map((key, index) => {
            const fact = facts.find(f => f.key === key);
            if (!fact || !fact.condition) return null;
            factIndex++;

            return (
                <QuickFactsItem
                    key={fact.key}
                    index={index.toString()}
                    devCounter={fact.devCounter}
                    itemLabel={fact.label}
                    itemFact={fact.fact}
                    tooltip={fact.tooltip}
                    onClick={fact.onClick && fact.onClick}
                    ref={fact.ref}
                    isHidden={!showMore && factIndex > numberOfVisibleItems} 
                />
            );
        })
        .filter(Boolean);
    };

    const getOrder = () => {
        // NO
        if(getCurrentLocale() === LOCALE.NO) {
            // Tomt
            if(viewModel.constructionForm === ResidenceType.PLOT) {
                return [
                    'minMaxPrice',  // Price
                    'coOwning',
                    'minMaxArea',
                    'address',
                    'plotArea',
                    'inhabitDate',
                ];
            } else return [
                'minMaxPrice',  // Price
                'coOwning',
                'minMaxRooms',
                'minMaxArea',
                'constructionForm',
                'disposalForm',
                'numberOfObjects',
                'inhabitDate',
                'address',
                'plotArea',
            ];
        }

        // FI
        if(getCurrentLocale() === LOCALE.FI) {
            if(!viewModel.jmoyGradinaProject) {
                return [
                    'numberOfObjects',
                    'inhabitDate',
                    'minMaxRooms',
                    'minMaxArea',
                    'minMaxPrice',
                    'fiMinMaxTotalPrice',
                    'nrOfParkingSpots',
                    'fiMinMaxTotalAmount',
                    'coOwning',
                    'fiLandOwnerShip',
                    'elevator',
                    'fiEnergyClass',
                    'swanLabel',
                    'address',
                ];
            }
            if(viewModel.jmoyGradinaProject) {
                return [
                    'numberOfObjects',
                    'inhabitDate',
                    'minMaxRooms',
                    'minMaxArea',
                    'minMaxPrice',
                    'fiMinMaxTotalPrice',
                    'fiLandOwnerShip',
                    'fiEnergyClass',
                    'swanLabel',
                    'address',
                ];
            }
        }

        return [
            'minMaxPrice',
            'minMaxFee',
            'coOwning',
            'inhabitDate',
            'plotArea',
            'minMaxRooms',
            'minMaxArea',
            'constructionForm',
            'disposalForm',
            'elevator',
            'energyMarking',
            'swanLabel',
            'address',
            'numberOfObjects',
        ];
    };

    const order = getOrder();

    return (
        <dl className="quick-facts__list">
            {renderFactsByOrder(order)}
        </dl>
    )
};